import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { URL } from "../config";

const API = `${URL}/primary-goals`;

const S = {
  page: {
    minHeight: "100vh",
    background: "#F4F6FB",
    padding: "20px",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  headerCard: {
    padding: "20px 24px 0 24px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 10,
  },
  headerUnderline: {
    width: "100%",
    borderTop: "2px solid #1A237E",
    marginTop: 16,
  },
  avatar: (isEdit) => ({
    width: 44,
    height: 44,
    borderRadius: 12,
    background: isEdit ? "#E53935" : "#1A237E",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    flexShrink: 0,
  }),
  pageTitle: { fontSize: 20, fontWeight: 700, color: "#0F1523", margin: 0 },
  breadcrumb: { fontSize: 13, color: "#5A6278", marginTop: 3 },
  breadcrumbActive: { color: "#1A237E", fontWeight: 600 },
  card: {
    background: "#fff",
    border: "1px solid #E8EBF4",
    borderRadius: 16,
    overflow: "hidden",
  },
  section: { padding: "22px 24px", background: "#fff" },
  sectionAlt: { padding: "22px 24px", background: "rgba(2,119,189,0.018)" },
  sectionDivider: { border: "none", borderTop: "1px dashed #E8EBF4", margin: 0 },
  sectionHeader: { display: "flex", alignItems: "center", gap: 10, marginBottom: 20 },
  sectionLine: { flex: 1, height: 1, background: "#E8EBF4" },
  sectionBadge: (color) => ({
    display: "flex", alignItems: "center", gap: 6,
    padding: "5px 14px", borderRadius: 20,
    border: `1px solid rgba(${color},0.2)`,
    background: `rgba(${color},0.06)`,
    whiteSpace: "nowrap",
  }),
  sectionBadgeText: (hex) => ({
    fontSize: 12, fontWeight: 700,
    letterSpacing: "0.08em", textTransform: "uppercase", color: hex,
  }),
  field: { display: "flex", flexDirection: "column", gap: 5 },
  fieldLabel: { fontSize: 15, fontWeight: 600, color: "#5A6278", margin: 0 },
  req: { color: "#E53935", marginLeft: 2 },
  inputWrap: { position: "relative", display: "flex", alignItems: "center" },
  inputIcon: {
    position: "absolute", left: 11, color: "#9BA3BE",
    pointerEvents: "none", zIndex: 1, display: "flex", alignItems: "center",
  },
  input: (focused) => ({
    width: "100%", height: 48, borderRadius: 10,
    border: `1.5px solid ${focused ? "#1A237E" : "#E0E4F0"}`,
    background: focused ? "#fff" : "#F8F9FD",
    color: "#0F1523", fontSize: 17, fontWeight: 500,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    outline: "none", padding: "0 12px 0 38px",
    boxSizing: "border-box",
    boxShadow: focused ? "0 0 0 3px rgba(26,35,126,0.08)" : "none",
    transition: "all 0.15s",
  }),
  toggleRow: {
    display: "flex", alignItems: "center", height: 48, borderRadius: 10,
    border: "1.5px solid rgba(2,119,189,0.18)",
    background: "rgba(2,119,189,0.05)",
    padding: "0 14px", gap: 14,
  },
  toggleItem: { display: "flex", alignItems: "center", gap: 7 },
  dot: (color, on) => ({
    width: 8, height: 8, borderRadius: "50%",
    background: on ? color : "#C5CAE9", transition: "background 0.2s",
  }),
  toggleLabel: { fontSize: 15, fontWeight: 600, color: "#5A6278" },
  toggleVdiv: { width: 1, background: "#E0E4F0", height: 18 },
  switchTrack: (on, color) => ({
    position: "relative", width: 34, height: 19, borderRadius: 10,
    background: on ? color : "#C5CAE9",
    cursor: "pointer", transition: "background 0.2s", flexShrink: 0,
  }),
  switchThumb: (on) => ({
    position: "absolute", width: 15, height: 15, borderRadius: "50%",
    background: "#fff", top: 2, left: on ? 17 : 2,
    transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
  }),
  footer: {
    padding: "14px 24px", borderTop: "1px solid #E8EBF4",
    background: "rgba(244,246,251,0.8)",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    flexWrap: "wrap", gap: 12,
  },
  footerNote: { display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#5A6278" },
  footerActions: { display: "flex", gap: 8 },
  btnCancel: {
    fontSize: 14, padding: "9px 18px", borderRadius: 20,
    border: "1.5px solid #E0E4F0", background: "transparent", color: "#0F1523",
    cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
    fontWeight: 600, fontFamily: "inherit",
  },
  btnSave: {
    fontSize: 14, padding: "9px 24px", borderRadius: 20,
    border: "none", background: "#fc100c", color: "#fff",
    cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
    fontWeight: 600, fontFamily: "inherit",
  },
};

const SectionHeader = ({ icon, label, hex, rgb }) => (
  <div style={S.sectionHeader}>
    <div style={S.sectionLine} />
    <div style={S.sectionBadge(rgb)}>
      <span style={{ color: hex, fontSize: 13, display: "flex" }}>{icon}</span>
      <span style={S.sectionBadgeText(hex)}>{label}</span>
    </div>
    <div style={S.sectionLine} />
  </div>
);

const Toggle = ({ on, onToggle, label, dotColor }) => (
  <div style={S.toggleItem}>
    <div style={S.dot(dotColor, on)} />
    <span style={S.toggleLabel}>{label}</span>
    <div
      style={S.switchTrack(on, dotColor)}
      onClick={onToggle}
      role="switch"
      aria-checked={on}
      tabIndex={0}
      onKeyDown={(e) => e.key === " " && onToggle()}
    >
      <div style={S.switchThumb(on)} />
    </div>
  </div>
);

const Icon = ({ d, size = 15, stroke = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={stroke} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);
const Icons = {
  target:   "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 18a6 6 0 1 0 0-12 6 6 0 0 0 0 12zM12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z",
  edit:     "M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z",
  addCircle:"M12 5v14M5 12h14M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z",
  shield:   "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
  list:     "M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01",
  save:     "M19 21H5a2 2 0 0 0-2 2v0M5 3h11l5 5v13a2 2 0 0 1-2 2z",
  info:     "M12 8v4M12 16h.01M22 12A10 10 0 1 1 2 12a10 10 0 0 1 20 0z",
};
const I = ({ name, size = 15, color }) => (
  <Icon d={Icons[name]} size={size} stroke={color || "currentColor"} />
);

/* ══════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════ */
const PrimaryGoalForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location?.state?.editData || null;
  const isEdit = !!editData?.primarygoalId;

  const [form, setForm] = useState({
    primarygoalName: editData?.primarygoalName || "",
    isActive:        editData ? editData.isActive  : 1,
    isDefault:       editData ? editData.isDefault : 0,
    isOn:            editData ? editData.isOn      : 1,
    lastChanged:     new Date().toISOString(),
  });

  const toggle = (key) => () => setForm((f) => ({ ...f, [key]: f[key] === 1 ? 0 : 1 }));

  const [toast, setToast] = useState(null);
  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const handleSave = async () => {
    if (!form.primarygoalName.trim()) return showToast("Primary goal name is required!", "error");

    const targetUrl = isEdit ? `${API}/${editData.primarygoalId}` : API;
    const payload = {
      ...form,
      modifiedBy: 1,
      createdBy: isEdit ? editData.createdBy : 1,
      lastChanged: new Date().toISOString(),
    };

    try {
      const res = await fetch(targetUrl, {
        method: isEdit ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        showToast(isEdit ? "Primary goal updated!" : "Primary goal saved!");
        setTimeout(() => navigate("/PrimaryGoal-details"), 1500);
      } else {
        showToast("Failed to save data!", "error");
      }
    } catch {
      showToast("API connection failed!", "error");
    }
  };

  const [focused, setFocused] = useState(false);

  return (
    <div style={S.page}>

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", top: 20, right: 20, zIndex: 9999,
          background: toast.type === "error" ? "#E53935" : "#1A237E",
          color: "#fff", padding: "10px 18px", borderRadius: 12,
          fontSize: 13, fontWeight: 600, fontFamily: "inherit",
          boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
        }}>
          {toast.msg}
        </div>
      )}

      {/* Single Card */}
      <div style={S.card}>

        {/* HEADER */}
        <div style={S.headerCard}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", gap: 12 }}>
            <div style={S.avatar(isEdit)}>
              <I name={isEdit ? "edit" : "target"} size={18} color="#fff" />
            </div>
            <div style={{ textAlign: "center" }}>
              <p style={S.pageTitle}>{isEdit ? "Update primary goal" : "Primary goal master"}</p>
              <p style={S.breadcrumb}>
                Master &rsaquo; Goals &rsaquo;{" "}
                <span style={S.breadcrumbActive}>{isEdit ? "Edit" : "Add new"}</span>
              </p>
            </div>
          </div>
          <div style={S.headerUnderline} />
        </div>

        {/* SECTION 1 — Goal Info */}
        <div style={S.section}>
          <SectionHeader icon={<I name="target" size={13} color="#1A237E" />} label="Goal information" hex="#1A237E" rgb="26,35,126" />
          <div style={{ maxWidth: 480 }}>
            <div style={S.field}>
              <label style={S.fieldLabel}>
                Primary goal name <span style={S.req}>*</span>
              </label>
              <div style={S.inputWrap}>
                <span style={S.inputIcon}><I name="target" size={15} /></span>
                <input
                  type="text"
                  value={form.primarygoalName}
                  onChange={(e) => setForm({ ...form, primarygoalName: e.target.value })}
                  placeholder=""
                  style={S.input(focused)}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                />
              </div>
            </div>
          </div>
        </div>

        <hr style={S.sectionDivider} />

        {/* SECTION 2 — Status */}
        <div style={S.sectionAlt}>
          <SectionHeader icon={<I name="shield" size={13} color="#0277BD" />} label="Status" hex="#0277BD" rgb="2,119,189" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, maxWidth: 640 }}>

            <div style={S.field}>
              <label style={S.fieldLabel}>Account status</label>
              <div style={S.toggleRow}>
                <Toggle on={form.isActive === 1} onToggle={toggle("isActive")} label="Active" dotColor="#fc100c" />
                <div style={S.toggleVdiv} />
                
              </div>
            </div>

            <div style={S.field}>
              {/* <label style={S.fieldLabel}>Default</label>
              <div style={{ ...S.toggleRow, border: "1.5px solid rgba(26,35,126,0.18)", background: "rgba(26,35,126,0.05)" }}>
                <Toggle on={form.isDefault === 1} onToggle={toggle("isDefault")} label="Set as default" dotColor="#1A237E" />
              </div> */}
            </div>

          </div>
        </div>

        {/* FOOTER */}
        <div style={S.footer}>
          <div style={S.footerNote}>
            <I name="info" size={13} color="#9BA3BE" />
            Fields marked <span style={{ color: "#E53935", fontWeight: 700, margin: "0 2px" }}>*</span> are required
          </div>
          <div style={S.footerActions}>
            <button style={S.btnCancel} onClick={() => navigate("/PrimaryGoal-details")}>
              <I name="list" size={14} /> View detail
            </button>
            <button style={S.btnSave} onClick={handleSave}>
              <I name="save" size={14} color="#fff" />
              {isEdit ? "Update" : "Save"}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default PrimaryGoalForm;